import { useCallback } from 'react'
import type { JSX } from 'react'

import { BellIcon, Link, Notice, PageHeader } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { useEmailNotificationPreferences } from '~publish/pages/Settings/EmailNotifications/hooks/useEmailNotificationPreferences'

import styles from './SingleChannelView.module.css'

const BANNER_ID = 'comment-notifications-banner'

/**
 * Get the settings URL for email notifications
 */
const getSettingsUrl = (): string => {
  return '/settings/notifications'
}

export const CommentNotificationsBanner = (): JSX.Element | null => {
  const banner = useDismissableBanner(BANNER_ID, {
    trackDismiss: true,
  })
  const { preferencesList, loading } = useEmailNotificationPreferences()

  const handleDismiss = useCallback((): void => {
    banner.dismiss()
  }, [banner])

  // Get environment-specific topic IDs

  // Find email preferences by cioTopicId to check subscription status
  // Banner shows only if BOTH dailyPostRecap and hourlyEmails are unsubscribed
  // We use the ids instead of the cioTopicId because the cioTopicId
  //  is not the sme across environments

  const hourlyEmailsPref =
    preferencesList?.find((pref) => pref.id === 'hourlyEmails') ?? null
  const dailyPostRecapPref =
    preferencesList?.find((pref) => pref.id === 'dailyPostRecap') ?? null

  // Don't show banner if:
  // - Still loading
  // - User has dismissed it
  // - Either preference is missing
  // - Either hourlyEmails or dailyPostRecap is already subscribed
  if (
    loading ||
    !banner.isActive ||
    hourlyEmailsPref === null ||
    dailyPostRecapPref === null ||
    hourlyEmailsPref.subscribed ||
    dailyPostRecapPref.subscribed
  ) {
    return null
  }

  const settingsUrl = getSettingsUrl()

  return (
    <PageHeader.Row className={styles.notice}>
      <Notice variant="neutral" icon={<BellIcon />} onDismiss={handleDismiss}>
        <Notice.Text>
          <strong>Never miss a comment.</strong> Turn on Community Notifications
          and choose how often you&apos;d like updates in{' '}
          <Link href={settingsUrl} className={styles.settingsLink}>
            your settings →
          </Link>
        </Notice.Text>
      </Notice>
    </PageHeader.Row>
  )
}
